import { Injectable, NgZone, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, from, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs';
import { SearchWhereResult, Experience } from './interfaces/experiences';
import { ExperiencesService } from './experiences.service';
import { environment } from '../environments/environment.common';

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class GooglePlacesService {
  private autocompleteService: any;
  private geocoder: any;
  private placesService: any;
  private scriptLoaded = false;
  private scriptLoading = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(
    private ngZone: NgZone,
    private experiencesService: ExperiencesService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializationPromise = this.loadGoogleMapsScript();
    }
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        this.initializeServices();
        resolve();
        return;
      }

      // Prevent multiple script loads
      if (this.scriptLoading) {
        // Wait for existing load to complete
        const checkInterval = setInterval(() => {
          if (this.scriptLoaded) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      this.scriptLoading = true;

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          this.initializeServices();
          this.scriptLoaded = true;
          this.scriptLoading = false;
          resolve();
        });
        existingScript.addEventListener('error', () => {
          this.scriptLoading = false;
          reject(new Error('Failed to load Google Maps script'));
        });
        return;
      }

      // Create and load the script
      const script = document.createElement('script');
      const apiKey = environment.values['googlePlacesApiKey'];
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=it`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        this.initializeServices();
        resolve();
      };
      script.onerror = () => {
        this.scriptLoading = false;
        reject(new Error('Failed to load Google Maps script'));
      };
      document.head.appendChild(script);
    });
  }

  private initializeServices() {
    // Wait for Google Maps to load
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.geocoder = new google.maps.Geocoder();
    } else {
      // Retry after a delay
      setTimeout(() => this.initializeServices(), 500);
    }
  }

  /**
   * Wait for the service to be ready
   */
  private async waitForInitialization(): Promise<boolean> {
    if (this.autocompleteService && this.geocoder) {
      return true;
    }

    if (this.initializationPromise) {
      try {
        await this.initializationPromise;
        // Give it a bit more time for services to initialize
        let retries = 0;
        while ((!this.autocompleteService || !this.geocoder) && retries < 20) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        return this.autocompleteService && this.geocoder;
      } catch (error) {
        console.error('Failed to initialize Google Places:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Search for places using Google Places Autocomplete
   */
  searchPlaces(input: string, language: string = 'it'): Observable<SearchWhereResult[]> {
    if (!input || input.trim().length < 2) {
      return of([]);
    }

    return from(this.waitForInitialization()).pipe(
      switchMap(isReady => {
        if (!isReady || !this.autocompleteService) {
          console.error('Google Places API failed to initialize');
          return of([]);
        }

        return new Observable<SearchWhereResult[]>(observer => {
          this.autocompleteService.getPlacePredictions(
            {
              input: input,
              componentRestrictions: { country: 'it' },
              language: language
            },
            (predictions: any[], status: any) => {
              this.ngZone.run(() => {
                if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                  const results = predictions.map(pred => this.mapPredictionToSearchResult(pred));
                  
                  // Load details and counts for each result
                  this.loadDetailsForResults(results).subscribe(
                    detailedResults => {
                      observer.next(detailedResults);
                      observer.complete();
                    },
                    error => {
                      observer.error(error);
                    }
                  );
                } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
                  observer.next([]);
                  observer.complete();
                } else {
                  console.error(`Places API error: ${status}`);
                  observer.next([]);
                  observer.complete();
                }
              });
            }
          );
        });
      }),
      catchError(error => {
        console.error('Error in searchPlaces:', error);
        return of([]);
      })
    );
  }

  /**
   * Load details for all results in parallel
   */
  private loadDetailsForResults(results: SearchWhereResult[]): Observable<SearchWhereResult[]> {
    if (!results || results.length === 0) {
      return of([]);
    }

    const detailObservables = results.map(result => 
      this.getPlaceDetailsWithCount(result.id, result.type || 'cities', 'it')
    );

    return from(Promise.all(detailObservables.map(obs => obs.toPromise()))).pipe(
      map(detailedResults => {
        return detailedResults.filter(r => r !== null) as SearchWhereResult[];
      }),
      catchError(() => of(results)) // Fallback to original results on error
    );
  }

  /**
   * Get place details with coordinates and experience count
   */
  getPlaceDetailsWithCount(placeId: string, type: string, language: string = 'it'): Observable<SearchWhereResult | null> {
    if (!this.geocoder) {
      return of(null);
    }

    return new Observable(observer => {
      this.geocoder.geocode(
        { placeId: placeId },
        (results: any[], status: any) => {
          this.ngZone.run(() => {
            if (status === 'OK' && results && results[0]) {
              const result = this.mapGeocodeToSearchResult(results[0], type);
              
              // Get experience count
              if (result.location.lat && result.location.lon) {
                const filter: Experience.Filter = {
                  page: 0,
                  size: 1,
                  lang: language as 'it' | 'de' | 'en',
                  latitude: result.location.lat,
                  longitude: result.location.lon,
                  radius: this.getRadiusForType(type)
                };

                this.experiencesService.listCard(filter).subscribe({
                  next: (response) => {
                    result.numExperiences = response.totalElements || 0;
                    observer.next(result);
                    observer.complete();
                  },
                  error: () => {
                    observer.next(result);
                    observer.complete();
                  }
                });
              } else {
                observer.next(result);
                observer.complete();
              }
            } else {
              observer.next(null);
              observer.complete();
            }
          });
        }
      );
    });
  }

  /**
   * Map Google Place prediction to SearchWhereResult
   */
  private mapPredictionToSearchResult(prediction: any): SearchWhereResult {
    const type = this.determinePlaceType(prediction.types);
    
    return {
      id: prediction.place_id,
      name: prediction.structured_formatting.main_text,
      location: { lat: 0, lon: 0 }, // Will be filled when details are fetched
      type: type,
      icon: this.getIconForType(type),
      numExperiences: 0
    };
  }

  /**
   * Map Google Geocode result to SearchWhereResult
   */
  private mapGeocodeToSearchResult(geocodeResult: any, type: string): SearchWhereResult {
    const location = geocodeResult.geometry.location;
    
    // Extract region and province from address components
    let region: string | undefined;
    let province: string | undefined;

    geocodeResult.address_components?.forEach((component: any) => {
      if (component.types.includes('administrative_area_level_1')) {
        region = component.long_name;
      }
      if (component.types.includes('administrative_area_level_2')) {
        province = component.long_name;
      }
    });

    return {
      id: geocodeResult.place_id,
      name: geocodeResult.formatted_address.split(',')[0],
      location: {
        lat: location.lat(),
        lon: location.lng()
      },
      type: type as any,
      icon: this.getIconForType(type as any),
      numExperiences: 0,
      region: region,
      province: province
    };
  }

  /**
   * Determine place type from Google Places types array
   */
  private determinePlaceType(types: string[]): 'cities' | 'provinces' | 'regions' | 'wine-regions' {
    if (types.includes('locality') || types.includes('sublocality') || types.includes('administrative_area_level_3')) {
      return 'cities';
    }
    if (types.includes('administrative_area_level_2')) {
      return 'provinces';
    }
    if (types.includes('administrative_area_level_1')) {
      return 'regions';
    }
    if (types.includes('country')) {
      return 'regions';
    }
    return 'cities';
  }

  /**
   * Get icon path based on place type
   */
  private getIconForType(type: 'cities' | 'provinces' | 'regions' | 'wine-regions'): string {
    // SVG icon as data URI
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHJ4PSIxMiIgZmlsbD0iI2YyZjJmMiIvPgogIDxwYXRoIGQ9Ik0zMiAxNGMtNy4yIDAtMTMgNS44LTEzIDEzIDAgOS41IDEzIDIzIDEzIDIzcyAxMy0xMy41IDEzLTIzYzAtNy4yLTUuOC0xMy0xMy0xM3oiCiAgICAgICAgZmlsbD0iI2JhMzI0ZiIvPgogIDxjaXJjbGUgY3g9IjMyIiBjeT0iMjciIHI9IjQuNSIgZmlsbD0iI2ZmZmZmZiIvPgo8L3N2Zz4=';
  }

  /**
   * Get radius based on place type
   */
  private getRadiusForType(type?: string): string {
    switch (type) {
      case 'cities':
        return '20km';
      case 'provinces':
        return '50km';
      case 'regions':
      case 'wine-regions':
        return '100km';
      default:
        return '20km';
    }
  }
}
